import { useEffect, useState } from 'react';
import { DateTime, DurationLikeObject } from 'luxon';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import moment from 'moment';
import {
  Box,
  CardContent,
  Grid,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis
} from 'recharts';
import DashboardTimeRange from '@bumble/app/components/DashboardTimeRange';
import { DASHBOARD_TIME_BUTTONS } from '@bumble/app/constants';
import { useApi } from '@bumble/app/hooks/use-api';
import { getTooltipLabel } from '@bumble/app/utils/get-date';
import { apiError } from '@bumble/app/utils/api-error';
import { useCpe } from './CpeContext';
import { Loading } from '../Loading';

interface LinkUtilizationDashboardProps {
  selectedTab: string;
}

export const LinkUtilizationDashboard = ({selectedTab}: LinkUtilizationDashboardProps) => {
  const [timeRange, setTimeRange] = useState<DurationLikeObject>(DASHBOARD_TIME_BUTTONS[0].value);
  const { cpe, setCpe } = useCpe();
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartData2, setChartData2] = useState<any[]>([]);
  const [chartData3, setChartData3] = useState<any[]>([]);
  const [chartData4, setChartData4] = useState<any[]>([]);
  const [isLoading1, setIsLoading1] = useState<boolean>(false);
  const [isLoading2, setIsLoading2] = useState<boolean>(false);
  const api = useApi();
  let oldCpeName: string, oldTimeRange: DurationLikeObject;

  const colors = ['#ECC052', '#88847D', '#5F467E', '#7EB670'];

  const apiCall1 = useMutation(api.dashboardGetNodePeakAvgUtilIndividualApiV1DashboardGetNodePeakAvgUtilIndividualGet, {
    onSuccess(data) {
      setIsLoading1(false);
      setChartData(data.data.map(n => ({
        rx_avg_util: parseFloat(n.rx_avg_util) * 100,
        rx_peak_util: parseFloat(n.rx_peak_util) * 100,
        timestamp: n.timestamp,
        tx_avg_util: parseFloat(n.tx_avg_util) * 100,
        tx_peak_util: parseFloat(n.tx_peak_util) * 100,
      })));
    },
    onError(e) {
      setIsLoading1(false);
      apiError(e);
      setChartData([]);
    },
  });

  const apiCall2 = useMutation(api.dashboardGetPeakHoursApiV1DashboardGetPeakHoursGet, {
    onSuccess(data) {
      const fullData = (data.data as any).peak_hours;
      const rxArr: any[] = [], txArr: any[] = [];
      const diffHour = new Date().getTimezoneOffset() / 60;
      for (let i in fullData.peak_hour_scatter_plot['rx.mbps']) {
        const j = (parseInt(i) + 24 - diffHour) % 24;
        fullData.peak_hour_scatter_plot['rx.mbps'][i].forEach((n: number) => {
          rxArr.push({ x: j * 3600, y: n, z: 200 });
        });
      }
      for (let i in fullData.peak_hour_scatter_plot['tx.mbps']) {
        const j = (parseInt(i) + 24 - diffHour) % 24;
        fullData.peak_hour_scatter_plot['tx.mbps'][i].forEach((n: number) => {
          txArr.push({ x: j * 3600, y: n, z: 200 });
        });
      }
      setChartData2(rxArr);
      setChartData3(txArr);
      setChartData4(fullData.peak_throughput_trend.map((n: any) => ({
        max_rx_bps: n['max.rx.bps'],
        max_tx_bps: n['max.tx.bps'],
        timestamp: new Date(n.timestamp).toISOString(),
      })).sort((a: any, b: any) => a.timestamp.localeCompare(b.timestamp)));
      setIsLoading2(false);
    },
    onError(e) {
      setIsLoading2(false);
      apiError(e);
      setChartData2([]);
      setChartData3([]);
    },
  });

  const handleTimeChange = (e: React.MouseEvent<HTMLElement>, val: DurationLikeObject) => {
    if (val) setTimeRange(val);
  };

  const handleCpeChange = (val: any) => {
    if (val) {
      setCpe({
        cpeName: val.label,
        nodeId: val.id,
      });
    }
  };

  useEffect(() => {
    if (cpe.cpeName && cpe.cpeName !== oldCpeName) {
      setIsLoading2(true);
      apiCall2.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
      });
      oldCpeName = cpe.cpeName;
    }
  }, [cpe]);

  useEffect(() => {
    if (cpe.cpeName && timeRange && (cpe.cpeName !== oldCpeName || timeRange !== oldTimeRange)) {
      setIsLoading1(true);
      apiCall1.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      oldCpeName = cpe.cpeName;
      oldTimeRange = timeRange;
    }
  }, [timeRange, cpe])

  const handleRefreshClick = () => {
    if (cpe.cpeName && timeRange) {
      setIsLoading1(true);
      apiCall1.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
        start_time: DateTime.now().minus(timeRange).toISO(),
        end_time: DateTime.now().toISO(),
      });
      setIsLoading2(true);
      apiCall2.mutate({
        node_id: cpe.nodeId,
        node_name: cpe.cpeName,
      });
    }
    toast.success('Refreshed chart data.');
  };

  function CustomizedTicks(props: any) {
    const { x, y, payload } = props;
    const convertedDate = typeof payload.value === 'string'
      ? new Date(payload.value).valueOf()
      : payload.value;
    const date = DateTime.fromMillis(convertedDate);
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} fill="#666">
          <tspan textAnchor="middle" x="0">
            {date?.toFormat('MMM d')}
          </tspan>
          <tspan textAnchor="middle" x="0" dy="20">
            {date?.toFormat('HH:mm')}
          </tspan>
        </text>
      </g>
    );
  }

  function CustomizedTicks2(props: any) {
    const { x, y, payload } = props;
    const convertedDate = typeof payload.value === 'string'
      ? new Date(payload.value).valueOf()
      : payload.value;
    const date = DateTime.fromMillis(convertedDate);
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={16} fill="#666">
          <tspan textAnchor="middle" x="0">
            {date?.toFormat('MMM d')}
          </tspan>
        </text>
      </g>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 0, py: 5 }}>
        <DashboardTimeRange
          timeValues={DASHBOARD_TIME_BUTTONS}
          selectedTime={timeRange}
          onCpeChange={handleCpeChange}
          onTimeChange={handleTimeChange}
          onRefresh={handleRefreshClick}
          selectedTab={selectedTab}
        />
      </Box>
      <CardContent sx={{bgcolor: '#fff', borderRadius: 1.2, mb: 5}}>
        <Grid container spacing={5} sx={{ marginTop: 1 }}>
          <Grid item xs={12} md={12}>
            <Box>
              <Typography color="primary" sx={{ mb: 5 }}>Upload/Download Utilization (%)</Typography>
              {isLoading1 ? <Loading /> :
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  margin={{ top: 5, right: 10, left: 20, bottom: 5, }}
                  data={chartData}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    type="category"
                    allowDuplicatedCategory={false}
                    height={60}
                    interval={7}
                    tick={<CustomizedTicks />}
                  />
                  <YAxis
                    orientation="left"
                    type="number"
                    domain={[0, 100]}
                    width={30}
                    tickFormatter={(value: number) => `${new Intl.NumberFormat('en-US', {
                      notation: 'compact',
                      compactDisplay: 'short',
                    }).format(value)}`}
                  />
                  <Line strokeWidth={2} stroke={colors[0]} dot={false} dataKey="tx_peak_util" name="Upload" />
                  <Line strokeWidth={2} stroke={colors[2]} dot={false} dataKey="rx_peak_util" name="Download" />
                  <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
                  <Legend wrapperStyle={{ bottom: -5 }} iconType={'rect'} />
                </LineChart>
              </ResponsiveContainer>}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
      <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2, mb: 5 }}>
        <Typography color="primary" sx={{ mb: 2 }}>Daily Site Traffic Peak Hour (Mbps)</Typography>
        {isLoading2 ? <Loading height={250} /> :
          <ResponsiveContainer width="100%" height={250}>
            <ScatterChart
              margin={{
                top: 20,
                right: 20,
                bottom: 20,
                left: 50,
              }}
            >
              <CartesianGrid />
              <XAxis type="number" dataKey="x"
                domain={[0, 82800]}
                ticks={[
                  0, 3600, 7200, 10800, 14400, 18000,
                  21600, 25200, 28800, 32400, 36000, 39600,
                  43200, 46800, 50400, 54000, 57600, 61200,
                  64800, 68400, 72000, 75600, 79200, 82800
                ]}
                tickFormatter={(tick) => moment(tick * 1000).utc().format('HH:mm')}
              >
                <Label value="Hours of the Day" offset={-10} position="insideBottom" />
              </XAxis>
              <YAxis type="number" dataKey="y" width={30}>
                <Label value="Peak Hour Throughput" angle={-90} offset={-25} position="insideBottomLeft" />
              </YAxis>
              <ZAxis type="number" dataKey="z" range={[100, 200]} />
              <Scatter name="Upload" data={chartData3} fill={colors[0]} />
              <Scatter name="Download" data={chartData2} fill={colors[2]} />
              <Legend wrapperStyle={{ bottom: -5 }} iconType={'circle'} />
            </ScatterChart>
          </ResponsiveContainer>
        }
      </CardContent>
      <CardContent sx={{ bgcolor: '#fff', borderRadius: 1.2, mb: 5 }}>
        <Typography color="primary" sx={{ mb: 2 }}>Peak Hour Throughput Trend (Mbps)</Typography>
        {isLoading2 ? <Loading height={250} /> :
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              margin={{ top: 5, right: 10, left: 20, bottom: 5 }}
              data={chartData4}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="timestamp"
                type="category"
                allowDuplicatedCategory={false}
                height={60}
                interval={0}
                tick={<CustomizedTicks2 />}
              />
              <YAxis
                orientation="left"
                type="number"
                width={30}
                tickFormatter={(value: number) => `${new Intl.NumberFormat('en-US', {
                  notation: 'compact',
                  compactDisplay: 'short',
                }).format(value)}`}
              />
              <Line strokeWidth={2} stroke={colors[0]} dot={false} dataKey="max_tx_bps" name="Upload" />
              <Line strokeWidth={2} stroke={colors[2]} dot={false} dataKey="max_rx_bps" name="Download" />
              <Tooltip labelFormatter={(label) => getTooltipLabel(label)} />
              <Legend wrapperStyle={{ bottom: -5 }} iconType={'rect'} />
            </LineChart>
          </ResponsiveContainer>
        }
      </CardContent>
    </Box>
  );
};
